import React, { useEffect, useState } from 'react';
import moment from "moment";
import * as API from '../../../api/billing';
import { Grid, Row, Col, FormGroup, ControlLabel, Modal, FormControl } from 'react-bootstrap';
import Datetime from 'react-datetime';
import Button from "Admin/elements/CustomButton/CustomButton.jsx";
import Loading from "react-loading";
import Select from "react-select";
import BillingReportPreview from './BillingPrivew.jsx';


const PREFERRED_SERVICE_ORDER = [
    "Consultation New",
    "Consultation Old",
    "Online Consultation New",
    "Online Consultation Old"
];

const BillingReport = ({ startDate, endDate, setStartDate, setEndDate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [billSubCategory, setBillSubCategory] = useState([]);
    const [billSubCategoryList, setBillSubCategoryList] = useState([]);
    const [services, setServices] = useState([]);
    const [formData, setFormData] = useState({ limit: 1000, page: 0, direction: "desc" });
    const [noActiveService, setNoActiveService] = useState(false)
    const [showPreviewModal, setShowPreviewModal] = useState(false)

    // Track the last search criteria to compare with current values
    const [lastSearchCriteria, setLastSearchCriteria] = useState({
        startDate: startDate,
        endDate: endDate,
        services: []
    });

    // Track if print button should be disabled
    const [isPrintDisabled, setIsPrintDisabled] = useState(false);

    const start_date = moment(startDate, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('YYYY-MM-DD');
    const end_date = moment(endDate, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('YYYY-MM-DD');

    // Check if search criteria have changed
    const checkCriteriaChanged = () => {
        const currentServices = billSubCategory.map(item => item.value).sort();
        const lastServices = lastSearchCriteria.services.sort();

        const datesChanged = startDate !== lastSearchCriteria.startDate || endDate !== lastSearchCriteria.endDate;
        const servicesChanged = JSON.stringify(currentServices) !== JSON.stringify(lastServices);

        setIsPrintDisabled(datesChanged || servicesChanged);
    };

    const handleStartDate = (date) => {
        let formatted = moment(date).format('YYYY-MM-DD');
        setStartDate(formatted);
    };

    const handleEndDate = (date) => {
        let formatted = moment(date).format('YYYY-MM-DD');
        setEndDate(formatted);
    };

    const valid = current => moment().format('YYYY-MM-DD') >= moment(current).format('YYYY-MM-DD');
    const valid2 = current => {
        let today = moment().format('YYYY-MM-DD');
        let currentFormatted = moment(current).format('YYYY-MM-DD');
        return today >= currentFormatted && start_date <= currentFormatted;
    };

    const getServiceList = async () => {
        setLoading(true);
        let resp = await API.serviceList();
        if (resp?.data?.status === "Success" && resp?.data?.statusCode) {
            let rawServices = resp.data.data?.filter(service => service?.isActive) || [];
            if (rawServices?.length === 0) {
                setNoActiveService(true)
                setLoading(false)
                setError("Currently, all services are deactivated. Please activate the services to view the billing summary.")
                return
            }
            const billSubCategoryOptions = rawServices.map((ele) => ({
                label: ele?.name,
                value: ele?._id,
                systemDefault: PREFERRED_SERVICE_ORDER.includes(ele?.systemDefaultName),
                ...ele
            }));

            const sortedBillSubCategoryOptions = billSubCategoryOptions.sort((a, b) => {
                const aIndex = PREFERRED_SERVICE_ORDER.indexOf(a.systemDefaultName);
                const bIndex = PREFERRED_SERVICE_ORDER.indexOf(b.systemDefaultName);

                if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
                if (aIndex !== -1) return -1;
                if (bIndex !== -1) return 1;

                return a.name.localeCompare(b.name);
            });

            setServices(rawServices);
            setBillSubCategoryList(sortedBillSubCategoryOptions);
        }
    };

    const getBillingReports = async () => {
        let selectedIds = billSubCategory.map(item => item.value);
        setError(null);
        setLoading(true);

        try {
            const response = await API.getBillReport({
                limit: formData.limit,
                offset: formData.page,
                order: "createdAt",
                direction: formData.direction,
                search: "",
                startDate: start_date,
                endDate: end_date,
                ids: selectedIds
            });

            const billList = response.data.data || [];
            let appliedServices = services;
            if (selectedIds.length > 0) {
                appliedServices = services.filter(service => selectedIds.includes(service._id));
            }

            const summary = mapServicesWithBills(appliedServices, billList);

            setData({
                from: moment(start_date).format("DD-MM-YYYY"),
                to: moment(end_date).format("DD-MM-YYYY"),
                summary: summary,
                totals: calculateTotals(billList, response.data.recovery),
                fromPrintDate: moment(start_date).format("DD-MMM-YYYY"),
                toPrintDate: moment(end_date).format("DD-MMM-YYYY"),
            });

            // Update last search criteria after successful search
            setLastSearchCriteria({
                startDate: startDate,
                endDate: endDate,
                services: billSubCategory.map(item => item.value)
            });

            // Enable print button after successful search
            setIsPrintDisabled(false);
        } catch (err) {
            setError("Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    const mapServicesWithBills = (services, billList) => {
        const sortedServices = [...services].sort((a, b) => {
            const aIndex = PREFERRED_SERVICE_ORDER.indexOf(a.systemDefaultName);
            const bIndex = PREFERRED_SERVICE_ORDER.indexOf(b.systemDefaultName);

            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;

            return a.name.localeCompare(b.name);
        });

        return sortedServices.map(service => {
            const patients = billList.flatMap(bill => bill.services
                .filter(serviceUsed => serviceUsed.serviceId === service._id)
                .map(serviceUsed => ({
                    price: Number(serviceUsed.price) || 0,
                    discount: Number(serviceUsed.discount) || 0,
                    quantity: Number(serviceUsed.quantity) || 1,
                    item: serviceUsed.item,
                    totalPaidAmount: Number(bill.totalPaidAmount) || 0,
                    installmentAmount: Number(bill.installmentAmount) || 0,
                    paymentMode: bill.paymentMode,
                    refundAmount: Number(bill.refundAmount) || 0,
                }))
            );

            const totalAmount = patients.reduce((acc, p) => acc + (p.price), 0);
            const totalCash = patients.filter(p => p.paymentMode === "Cash").reduce((acc, p) => acc + p.totalPaidAmount, 0);
            const totalUPI = patients.filter(p => p.paymentMode === "UPI").reduce((acc, p) => acc + p.totalPaidAmount, 0);
            const totalCard = patients.filter(p => p.paymentMode === "Card").reduce((acc, p) => acc + p.totalPaidAmount, 0);
            const totalRecovery = patients.reduce((acc, p) => acc + p.installmentAmount, 0);
            const totalDiscount = patients.reduce((acc, p) => acc + p.discount, 0);
            const totalRefund = patients.reduce((acc, p) => acc + p.refundAmount, 0);

            return {
                id: service._id,
                service: service.name,
                patientCount: patients.length,
                totalAmount,
                totalCash,
                totalUPI,
                totalCard,
                totalRecovery,
                totalDiscount,
                patients,
                totalRefund
            };
        });
    };

    const calculateTotals = (billList, recovery) => {
        const totalRecovery = recovery.reduce((acc, row) => acc + (Number(row.amountPaid) || 0), 0);
        const totalRecoveryCASH = recovery.filter(row => row.paymentMode === "Cash").reduce((acc, row) => acc + (Number(row.amountPaid) || 0), 0);
        const totalRecoveryUPI = recovery.filter(row => row.paymentMode === "UPI").reduce((acc, row) => acc + (Number(row.amountPaid) || 0), 0);
        const totalRecoveryCARD = recovery.filter(row => row.paymentMode === "Card").reduce((acc, row) => acc + (Number(row.amountPaid) || 0), 0);

        let totalRemaining = 0;

        for (const bill of billList) {
            totalRemaining += (Number(bill.totalRemainingDuringBilling) || 0);
            for (const row of recovery) {
                if (row.billNumber === bill.billNumber) {
                    totalRemaining -= (Number(row.amountPaid) || 0);
                }
            }
        }

        let totals = {
            totalCollection: billList.reduce((acc, bill) => acc + (Number(bill.totalPaidDuringBilling) || 0), 0) + totalRecovery,
            totalBilling: billList.reduce((acc, bill) => acc + (Number(bill.totalAmount) || 0), 0),
            totalCash: billList.filter(bill => bill.paymentMode === "Cash").reduce((acc, bill) => acc + (Number(bill.totalPaidDuringBilling) || 0), 0) + + totalRecoveryCASH,
            totalUPI: billList.filter(bill => bill.paymentMode === "UPI").reduce((acc, bill) => acc + (Number(bill.totalPaidDuringBilling) || 0), 0) + totalRecoveryUPI,
            totalCard: billList.filter(bill => bill.paymentMode === "Card").reduce((acc, bill) => acc + (Number(bill.totalPaidDuringBilling) || 0), 0) + totalRecoveryCARD,
            totalRecovery: totalRecovery,
            totalDiscount: billList.reduce((acc, bill) => acc + (Number(bill.services?.reduce((sum, service) => sum + service.discount, 0)) || 0), 0),
            totalRefund: billList.reduce((acc, bill) => acc + (Number(bill.refundAmount) || 0), 0),
            totalRemaining: totalRemaining <= 0 ? 0 : totalRemaining,
        };

        return totals;
    };

    useEffect(() => {
        getServiceList();
    }, []);

    useEffect(() => {
        if (services.length > 0) {
            getBillingReports();
        }
    }, [services]);

    // Check for criteria changes whenever dates or services change
    useEffect(() => {
        checkCriteriaChanged();
    }, [startDate, endDate, billSubCategory]);

    const handlePrint = () => {
        if (data) {
            setShowPreviewModal(true);
        }
    };


    console.log(billSubCategory, "---billSub")

    return (
        <div className="billing-container">
            <Row>
                <Col sm={12} md={4} lg={3}>
                    <FormGroup className="d-flex align-item-baseline col-gap-5 self-mb-0">
                        <Col componentClass={ControlLabel}>From:</Col>
                        <Datetime
                            timeFormat={false}
                            dateFormat={"DD-MM-YYYY"}
                            value={moment(startDate, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('DD-MM-YYYY') || ""}
                            onChange={handleStartDate}
                            isValidDate={valid}
                            inputProps={{ placeholder: "DD-MM-YYYY" }}
                            key={'start_Date' + startDate}
                        />
                    </FormGroup>
                </Col>
                <Col sm={12} md={4} lg={3}>
                    <FormGroup className="d-flex align-item-baseline col-gap-5 self-mb-0">
                        <Col componentClass={ControlLabel}>To:</Col>
                        <Datetime
                            timeFormat={false}
                            dateFormat={"DD-MM-YYYY"}
                            value={moment(endDate, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('DD-MM-YYYY') || ""}
                            onChange={handleEndDate}
                            isValidDate={valid2}
                            inputProps={{ placeholder: "DD-MM-YYYY" }}
                            maxDate={new Date()}
                            key={'end_Date' + endDate}
                        />
                    </FormGroup>
                </Col>
                <Col sm={12} md={6} lg={3}>
                    <FormGroup className="d-flex align-item-baseline col-gap-5 self-mb-0">
                        <Col componentClass={ControlLabel}>Service:</Col>
                        <Select
                            options={billSubCategoryList}
                            value={billSubCategory}
                            onChange={setBillSubCategory}
                            isClearable
                            isSearchable
                            multi={true}
                            className="w-100"
                        />
                    </FormGroup>
                </Col>
                <Col sm={12} md={6} lg={3} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Col sm={6} md={8} lg={8}>
                        <FormGroup className="self-mb-0 text-right">
                            <Button
                                className="btn btn-fill btn-primary"
                                onClick={getBillingReports}
                            >
                                Search
                            </Button>
                        </FormGroup>
                    </Col>
                    <Col sm={6} md={4} lg={4}>
                        <FormGroup className="self-mb-0 text-right">
                            <Button
                                className={`btn btn-fill ${isPrintDisabled ? 'btn-default' : 'btn-primary'}`}
                                onClick={handlePrint}
                                disabled={isPrintDisabled}
                                title={isPrintDisabled ? "Please perform a search with current criteria before printing" : "Print billing report"}
                            >
                                <span role="img" aria-label="print" style={{ filter: isPrintDisabled ? 'grayscale(100%)' : 'none' }}>
                                    🖨️ Print
                                </span>
                            </Button>
                            {isPrintDisabled && (
                                <div style={{ fontSize: "11px", color: "#666", marginTop: "5px" }}>
                                    ⚠️ Search required
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                </Col>
            </Row>

            {loading ? (
                <div className="loading-container">
                    <Loading
                        type="bars"
                        color="#000000"
                        style={{ margin: "0px auto", width: "40px" }}
                    />
                </div>
            ) : error ? (
                noActiveService ?
                    <p style={{ marginTop: "10px", fontSize: "16px", color: "#856404" }}>
                        <i className="fa fa-exclamation-triangle text-warning" style={{ fontSize: "24px", marginRight: "10px" }}></i>
                        {error}
                    </p>
                    :
                    <div className="error-message">{error}</div>
            ) : (data && data.summary && data.summary.length > 0) ? (
                <>
                    <h4 className="billing-heading self-my-3">Billing Summary</h4>
                    <table className="billing-table">
                        <thead className="border-top-2px-blue">
                            <tr>
                                <th>#</th>
                                <th>Service</th>
                                <th>No. Of Patient</th>
                                <th>Amount (INR)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.summary.map((item, idx) => (
                                <tr key={idx}>
                                    <td>{idx + 1}</td>
                                    <td>{item?.service}</td>
                                    <td>{item.patientCount}</td>
                                    <td>{item.totalAmount.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div
                        className="totals-container d-flex flex-column"
                        style={{ marginTop: 18, gap: 12, fontSize: 13, alignItems: "flex-end" }}
                    >
                        <div
                            className="total-item"
                            style={{
                                background: "#f4f8fb",
                                borderRadius: 4,
                                textAlign: "right",
                            }}
                        >
                            <strong>Total Collection:</strong> {data.totals.totalCollection.toFixed(2)}
                        </div>
                        <div
                            className="total-item"
                            style={{
                                background: "#f4f8fb",
                                borderRadius: 4,
                                textAlign: "right",
                            }}
                        >
                            <strong>Total Billing:</strong> {data.totals.totalBilling.toFixed(2)}
                        </div>
                        <div
                            className="total-item"
                            style={{
                                background: "#f4f8fb",
                                borderRadius: 4,
                                textAlign: "right",
                            }}
                        >
                            <strong>Total Cash:</strong> {data.totals.totalCash.toFixed(2)}
                        </div>
                        <div
                            className="total-item"
                            style={{
                                background: "#f4f8fb",
                                borderRadius: 4,
                                textAlign: "right",
                            }}
                        >
                            <strong>Total UPI:</strong> {data.totals.totalUPI.toFixed(2)}
                        </div>
                        <div
                            className="total-item"
                            style={{
                                background: "#f4f8fb",
                                borderRadius: 4,
                                textAlign: "right",
                            }}
                        >
                            <strong>Total Card:</strong> {data.totals.totalCard.toFixed(2)}
                        </div>
                        <div
                            className="total-item"
                            style={{
                                background: "#f4f8fb",
                                borderRadius: 4,
                                textAlign: "right",
                            }}
                        >
                            <strong>Total Discount:</strong> {data.totals.totalDiscount.toFixed(2)}
                        </div>
                        <div
                            className="total-item"
                            style={{
                                background: "#f4f8fb",
                                borderRadius: 4,
                                textAlign: "right",
                            }}
                        >
                            <strong>Total Refund:</strong> {data.totals.totalRefund.toFixed(2)}
                        </div>
                        <div
                            className="total-item"
                            style={{
                                background: "#f4f8fb",
                                borderRadius: 4,
                                textAlign: "right",
                            }}
                        >
                            <strong>Total Recovery:</strong> {data.totals.totalRecovery.toFixed(2)}
                        </div>
                        <div
                            className="total-item"
                            style={{
                                background: "#f4f8fb",
                                borderRadius: 4,
                                textAlign: "right",
                            }}
                        >
                            <strong>Total Remaining:</strong> {data.totals.totalRemaining.toFixed(2)}
                        </div>
                    </div>

                </>
            ) : (
                <div className="no-data-message" style={{ marginTop: "10px", fontSize: "16px", color: "#856404" }}>
                    No billing data available for the selected date range.
                </div>
            )}
            <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} dialogClassName="modal-lg">
                <Modal.Body>
                    <BillingReportPreview data={data} setShowPreviewModal={setShowPreviewModal} />
                </Modal.Body>
            </Modal>


        </div >
    );
};

export default BillingReport;
